/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-64 — the TinyPrint X5's actual protocol (the "cat printer" family)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * THIS REPLACES THE ESC/POS RASTER, WHICH WAS SIMPLY WRONG FOR THIS HARDWARE.
 *
 * Proven on the owner's X5 on 2026-09-11: the tablet connected, wrote 19,645
 * bytes of correctly-formed ESC/POS `GS v 0` raster, reported success — and
 * printed nothing. A follow-up probe sent a short `ESC @` + text job down EVERY
 * writable channel the printer exposes; not one produced paper. So the
 * transport, the pairing and the channel were all right, and the command
 * language was wrong. G-33's original research assumed ESC/POS from a
 * reference implementation for a different printer family; that assumption is
 * now disproven on the real device.
 *
 * The X5 is a "cat printer": service `ae30`, characteristic `ae01` — exactly
 * what pairing discovered — speaking a packet protocol of its own:
 *
 *     0x51 0x78 <cmd> 0x00 <len> 0x00 <payload…> <crc8(payload)> 0xFF
 *
 * Two details are easy to get wrong and silently fatal, because a malformed
 * packet is DISCARDED rather than rejected — which is precisely the symptom we
 * spent the afternoon chasing:
 *   1. the CRC covers the PAYLOAD ONLY, not the header;
 *   2. bitmap bytes are sent bit-REVERSED, so the leftmost dot is the least
 *      significant bit — the opposite of ESC/POS.
 *
 * Sources: the Cat-Printer project's `commander.py` (CC0) and the community
 * protocol wiki. We implement it rather than depend on either: this is ~100
 * lines and the reference is Python.
 */

/**
 * 58 mm paper is 384 dots, which is 48 bytes of bitmap per row — the default,
 * and what both of the shop's printers use (owner, 2026-09-12).
 *
 * It is a DEFAULT rather than a law because the packing below truncates a
 * bitmap wider than the head and pads a narrower one, in both cases WITHOUT
 * ERROR. A job built for the wrong width is therefore perfectly well-formed and
 * silently wrong, which is the same failure mode that cost an afternoon when
 * the protocol itself was wrong.
 */
export const CAT_ROW_BYTES = 48;

/** CRC-8, polynomial 0x07, initial value 0, no reflection, no final xor. */
const CRC8_TABLE: number[] = (() => {
  const table = new Array<number>(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let bit = 0; bit < 8; bit += 1) {
      c = c & 0x80 ? ((c << 1) ^ 0x07) & 0xff : (c << 1) & 0xff;
    }
    table[i] = c;
  }
  return table;
})();

export function crc8(data: Uint8Array | number[]): number {
  let crc = 0;
  for (const byte of data) crc = CRC8_TABLE[(crc ^ byte) & 0xff];
  return crc & 0xff;
}

/**
 * Wraps a payload in the printer's envelope.
 *
 * The length field is one byte, so a payload over 255 is impossible — which is
 * why the bitmap goes ONE ROW PER COMMAND (48 bytes) rather than as one image
 * the way ESC/POS would send it.
 */
export function catCommand(command: number, payload: number[] | Uint8Array): Uint8Array {
  const body = Array.from(payload);
  if (body.length > 0xff) {
    throw new Error(`Cat printer payload too big: ${body.length} > 255`);
  }
  return Uint8Array.from([
    0x51,
    0x78,
    command & 0xff,
    0x00,
    body.length & 0xff,
    0x00,
    ...body,
    crc8(body),
    0xff,
  ]);
}

/**
 * Little-endian, in the width the command expects.
 *
 * Clamps to what the REQUESTED width can hold, not to a fixed 0xffff. The
 * earlier version capped everything at two bytes whatever it was asked for, so
 * a one-byte call would have emitted a silently truncated value and a four-byte
 * call two wasted zeros — and on this printer a wrong byte is a discarded
 * packet with no error, which is the failure mode that cost an afternoon.
 */
function leBytes(value: number, length: number): number[] {
  const max = 2 ** (length * 8) - 1;
  let v = Math.max(0, Math.min(Math.round(value), max));
  const out: number[] = [];
  for (let i = 0; i < length; i += 1) {
    out.push(v & 0xff);
    v = Math.floor(v / 256);
  }
  return out;
}

/**
 * Packs one row of pixels, leftmost dot into the LEAST significant bit.
 *
 * This is the reversal the reference does with a `reverse_bits` helper after
 * packing the other way round. Doing it directly is the same result with half
 * the work — and it is the single most likely thing to get backwards, so it is
 * tested.
 */
export function packCatRow(
  pixels: Uint8Array,
  width: number,
  y: number,
  rowBytes: number = CAT_ROW_BYTES,
): number[] {
  const row = new Array<number>(rowBytes).fill(0);
  for (let x = 0; x < Math.min(width, rowBytes * 8); x += 1) {
    if (!pixels[y * width + x]) continue;
    row[x >> 3] |= 1 << (x & 7);
  }
  return row;
}

/** Magic from the reference driver. Not decodable, not ours to change. */
const LATTICE_START = [0xaa, 0x55, 0x17, 0x38, 0x44, 0x5f, 0x5f, 0x5f, 0x44, 0x38, 0x2c];
const LATTICE_END = [0xaa, 0x55, 0x17, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x17];

export interface CatJobOptions {
  /** Burn strength. The reference reports the factory default near 0x3000. */
  energy?: number;
  /** Dots of blank paper after the ticket, so the last line clears the tear bar. */
  feed?: number;
  /** Feed speed. Counter-intuitively, LOWER is quicker. */
  speed?: number;
  /** Bytes per printed row — the HEAD's width, not the bitmap's. 48 = 384 dots. */
  rowBytes?: number;
}

/**
 * Builds a complete print job.
 *
 * The ORDER is the reference driver's and is not decoration: the printer only
 * accepts a bitmap between a start and an end lattice, and only applies the
 * energy and speed it was told about BEFORE the lattice opened. Rows sent
 * outside that window are swallowed in silence.
 */
export function toCatPrinterJob(
  bitmap: { width: number; height: number; pixels: Uint8Array },
  options: CatJobOptions = {},
): Uint8Array {
  const energy = options.energy ?? 0x3000;
  const feed = options.feed ?? 120;
  const speed = options.speed ?? 32;
  const rowBytes = options.rowBytes ?? CAT_ROW_BYTES;
  const parts: Uint8Array[] = [];

  parts.push(catCommand(0xa3, [0x00])); // wake and refresh state
  parts.push(catCommand(0xa4, [50])); // quality
  parts.push(catCommand(0xbd, [speed])); // feed speed
  parts.push(catCommand(0xaf, leBytes(energy, 2))); // thermal energy
  parts.push(catCommand(0xbe, [0x01])); // apply the energy just set
  parts.push(catCommand(0xa9, [0x00])); // commit the config
  parts.push(catCommand(0xa6, LATTICE_START));

  for (let y = 0; y < bitmap.height; y += 1) {
    parts.push(
      catCommand(0xa2, packCatRow(bitmap.pixels, bitmap.width, y, rowBytes)),
    );
  }

  parts.push(catCommand(0xa6, LATTICE_END));
  parts.push(catCommand(0xbd, [8])); // feed quickly now the printing is done
  parts.push(catCommand(0xa1, leBytes(feed, 2)));
  parts.push(catCommand(0xa3, [0x00]));

  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}
